/**
 * ResultScreen.tsx - Results display for the Ultimatum experiment (Corporate Design)
 * Last updated: 2025-08-14
 * Updated by: JulianPolly
 */

import React, { useEffect, useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { Switch } from "@blueprintjs/core";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { Ultimatum } from "../../../../translations/experiments/Ultimatum";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles, chartColors } from "../../../../components/colors";


const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 650,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const chartStyle: React.CSSProperties = {
    minWidth: 400,
    maxWidth: 780,
    flex: 1
};

const barColorAnbieter = chartColors.primary[0];      // colors.lichtBlau
const barColorEmpfaenger = chartColors.alternative[0]; // colors.primary (PH Grün)

// Label only if > 0, centered over bar
const OnlyNonZeroLabel = (props: any) => {
    const { value, x, y, width, fill } = props;
    if (!value || value === 0) return null;
    return (
        <text
            x={x + (width ? width / 2 : 0)}
            y={y}
            dy={-8}
            fill={fill || "#222"}
            fontSize={15}
            textAnchor="middle"
            fontWeight="bold"
            style={{ pointerEvents: "none" }}
        >
            {value}
        </text>
    );
};

const TOTAL = 20;

// Full axis: 20/0 to 0/20
function getFullVerteilungList() {
    const arr: { verteilung: string, Anbieter: number, Empfänger: number, anbieter: number }[] = [];
    for (let anbieter = TOTAL; anbieter >= 0; anbieter--) {
        arr.push({
            verteilung: `${anbieter} / ${TOTAL - anbieter}`,
            Anbieter: 0,
            Empfänger: 0,
            anbieter
        });
    }
    return arr;
}

// Extract histogram from all users
function extractDistributions(users: any[]): { [verteilung: string]: { Anbieter: number, Empfänger: number } } {
    const hist: { [verteilung: string]: { Anbieter: number, Empfänger: number } } = {};
    users.forEach(user => {
        const ult = user.Ultimatum;
        if (!ult) return;
        Object.values(ult).forEach((caseObj: any) => {
            if (!caseObj.selections || !Array.isArray(caseObj.selections)) return;
            caseObj.selections.forEach((sel: any) => {
                if (typeof sel.amountForSelf === "number") {
                    const anbieter = sel.amountForSelf;
                    const empfaenger = TOTAL - anbieter;
                    const label = `${anbieter} / ${empfaenger}`;
                    if (!hist[label]) hist[label] = { Anbieter: 0, Empfänger: 0 };
                    hist[label].Anbieter++;
                }
                if (typeof sel.minAcceptableAmount === "number") {
                    const empfaenger = sel.minAcceptableAmount;
                    const anbieter = TOTAL - empfaenger;
                    const label = `${anbieter} / ${empfaenger}`;
                    if (!hist[label]) hist[label] = { Anbieter: 0, Empfänger: 0 };
                    hist[label].Empfänger++;
                }
            });
        });
    });
    return hist;
}

// Info sentence for user's own selections
function getUltimatumInfoText(userData: any, t: any, language: string) {
    const selections: { anbieter?: number, empfaenger?: number, role: "Anbieter" | "Empfänger" }[] = [];
    const ult = userData?.Ultimatum;
    if (!ult) return t.yourChoicesNone;
    Object.values(ult).forEach((caseObj: any) => {
        if (!caseObj.selections || !Array.isArray(caseObj.selections)) return;
        caseObj.selections.forEach((sel: any) => {
            if (typeof sel.amountForSelf === "number") {
                selections.push({
                    anbieter: sel.amountForSelf,
                    empfaenger: TOTAL - sel.amountForSelf,
                    role: "Anbieter"
                });
            }
            if (typeof sel.minAcceptableAmount === "number") {
                selections.push({
                    anbieter: TOTAL - sel.minAcceptableAmount,
                    empfaenger: sel.minAcceptableAmount,
                    role: "Empfänger"
                });
            }
        });
    });

    if (selections.length === 0) {
        return t.yourChoicesNone;
    }

    // Use translation function if available (in t.yourChoicesSome) else fallback to local code
    if (typeof t.yourChoicesSome === "function") {
        // Convert to the expected array format
        return (
            <span dangerouslySetInnerHTML={{
                __html: t.yourChoicesSome(
                    selections.map(sel => ({
                        role: sel.role,
                        anbieter: sel.anbieter ?? 0,
                        empfaenger: sel.empfaenger ?? 0,
                    }))
                )
            }} />
        );
    }

    // fallback
    return (
        <span>
            {selections.map((sel, i) =>
                <span key={i}>
                    {language === "de" ? "Du warst" : "You were"} <b>{sel.role === "Anbieter" ? (language === "de" ? "als Anbieter:in" : "as proposer") : (language === "de" ? "als Empfänger:in" : "as recipient")}</b> {language === "de" ? "aktiv und hast die Verteilung" : "and chose the distribution"} <b>{sel.anbieter} € / {sel.empfaenger} €</b> (Anbieter/Empfänger) {language === "de" ? "gewählt." : "chosen."}
                    {i < selections.length - 1 ? <br /> : null}
                </span>
            )}
        </span>
    );
}

// Custom Tooltip with percentages for corporate design
const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        return (
            <div style={{
                background: "#fff",
                border: `1.5px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color || colors.textMedium }}>
                    {value} ({percentage}%)
                </div>
            </div>
        );
    }
    return null;
};

// Switch card style for toggles
const switchCardStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.7)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    borderRadius: 10,
    border: "1px solid #e1e6ea",
    padding: "18px 34px",
    margin: "28px auto 18px auto",
    display: "flex",
    justifyContent: "center",
    gap: 36,
    width: "fit-content"
};

const UltimatumResultScreenComponent: React.FC = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    const t = Ultimatum[language].screens.results;
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [showAnbieter, setShowAnbieter] = useState(true);
    const [showEmpfaenger, setShowEmpfaenger] = useState(true);
    const [, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            setAllUsers(data || []);
            setLoading(false);
        }).catch(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    // Histogramm extrahieren
    const histogram = useMemo(() => extractDistributions(allUsers), [allUsers]);

    // Plot-Daten: vollständige Achse, Werte aus Histogramm oder 0
    const chartData = useMemo(() => getFullVerteilungList().map(item => ({
        verteilung: item.verteilung,
        Anbieter: showAnbieter ? (histogram[item.verteilung]?.Anbieter || 0) : 0,
        Empfänger: showEmpfaenger ? (histogram[item.verteilung]?.Empfänger || 0) : 0,
        anbieter: item.anbieter
    })), [histogram, showAnbieter, showEmpfaenger]);

    // Total for tooltip percentage
    const totalBars = useMemo(() =>
            chartData.reduce((sum, d) => sum + (d.Anbieter || 0) + (d.Empfänger || 0), 0),
        [chartData]
    );

    // Info-Satz
    const infoText = getUltimatumInfoText(userData, t, language);

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t.title}
            </h2>
            <div style={{
                fontSize: 16.5,
                color: colors.textMedium,
                fontStyle: "italic",
                marginBottom: 14,
                textAlign: "center"
            }}
                 dangerouslySetInnerHTML={{ __html: t.subTitle }} />
            <div style={switchCardStyle}>
                <Switch
                    checked={showAnbieter}
                    label={t.switchAnbieter}
                    onChange={() => setShowAnbieter(v => !v)}
                    style={{ fontWeight: 500, fontSize: 16, marginRight: 16 }}
                />
                <Switch
                    checked={showEmpfaenger}
                    label={t.switchEmpfaenger}
                    onChange={() => setShowEmpfaenger(v => !v)}
                    style={{ fontWeight: 500, fontSize: 16 }}
                />
            </div>
            <div style={{
                ...chartBlockStyle,
                marginBottom: 8
            }}>
                <div style={{ ...chartStyle, margin: "0 auto" }}>
                    <ResponsiveContainer width="100%" height={360}>
                        <BarChart data={chartData} barCategoryGap="6%">
                            <XAxis
                                dataKey="verteilung"
                                tick={{ fontSize: 14, angle: -30, textAnchor: "end" } as any}
                                interval={0}
                                height={80}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                                content={<CustomTooltip total={totalBars} />}
                                cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                            />
                            <Legend
                                wrapperStyle={{
                                    fontSize: 15,
                                    fontWeight: 500,
                                    marginBottom: 8
                                }}
                            />
                            {showAnbieter && (
                                <Bar
                                    dataKey="Anbieter"
                                    fill={barColorAnbieter}
                                    maxBarSize={28}
                                    radius={[3, 3, 0, 0]}
                                    name={t.switchAnbieter}
                                >
                                    <LabelList dataKey="Anbieter" content={OnlyNonZeroLabel} />
                                </Bar>
                            )}
                            {showEmpfaenger && (
                                <Bar
                                    dataKey="Empfänger"
                                    fill={barColorEmpfaenger}
                                    maxBarSize={28}
                                    radius={[3, 3, 0, 0]}
                                    name={t.switchEmpfaenger}
                                >
                                    <LabelList dataKey="Empfänger" content={OnlyNonZeroLabel} />
                                </Bar>
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div style={resultMessageStyle}>
                {infoText}
            </div>
        </div>
    );
};

export const UltimatumResultScreen: ScreenConfig = {
    component: UltimatumResultScreenComponent,
    defaultResult: {},
    canContinueDefault: true,
};